import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";

// Importing modal components
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../modals/AnimatedModal";

// Importing common helpers and validators
import { STRING_NOT_NULL_HELPER } from "../../../common/helpersTxt";

// Importing components
import ItemRowWithCheckbox from "../complements/itemRowWithCheckbox";
import ColorPicker from "../../Inputs/colorPicker";

// API
import api from "../../../store/services/api";

// Material UI components
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { OutlinedInput } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PaletteIcon from "@mui/icons-material/Palette";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

// Tab panel component for the tabbed interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`category-tabpanel-${index}`}
      aria-labelledby={`category-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </Box>
      )}
    </div>
  );
}

export default function CategoryModal({
  show,
  closeModal,
  category,
  createCategory,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);

  const [productTitle, setProductTitle] = useState("");
  const [hexColor, setHexColor] = useState(
    category ? category.color_hex : "#4CAF50" // Default to a nice green
  );

  const [productsExistedList, setProductsExistedList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [categoriesListToDelete, setCategoriesListToDelete] = useState([]);

  const [submitted, setSubmitted] = useState(false);

  const [title, setTitle] = useState(category?.title || "");
  const [isTitleValid, setIsTitleValid] = useState(false);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    setTimeout(async () => {
      await loadData();
    }, 10);
  }, []);

  const loadData = async () => {
    try {
      const filters = `?page=${page}`;
      let treatedProductCategories = [];

      if (category) {
        const { data } = await api.get("/products/categories/product/", {
          params: { category_id: category.id },
        });
        treatedProductCategories = data.map((product) => ({
          ...product,
          existed: true,
          checked: true,
        }));
        setIsEditing(true);
      }

      setProductsExistedList(treatedProductCategories);

      const { data } = await api.get("/products" + filters);
      const newData = data.filter(
        (item) =>
          !treatedProductCategories.some(
            (elem) => elem.product === item.product_id
          )
      );
      setProductsList([...treatedProductCategories, ...newData]);
    } catch (error) {
      setErrorMessage("Erro ao carregar dados");
      setOpenErrorAlert(true);
    }
  };

  const searchProducts = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      setPage(1);
      const filters = `?page=${page}&title=${productTitle}`;
      const { data } = await api.get("/products" + filters);
      const newData = data.filter(
        (item) =>
          !productsExistedList.some((elem) => elem.product === item.product_id)
      );
      setProductsList([...newData, ...productsExistedList]);
    } catch (error) {
      setErrorMessage("Erro ao buscar produtos");
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const addCategoryProductsToDelete = () => {
    const categoryProductsToAdd = productsList.filter(
      (product) => !product.checked && product.existed
    );
    setCategoriesListToDelete((prevList) => [
      ...prevList,
      ...categoryProductsToAdd,
    ]);
  };

  const handleIsChecked = (value, index) => {
    const _productsList = productsList;
    _productsList[index].checked = value;
    setProductsList(_productsList);
    addCategoryProductsToDelete();
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    if (!title) {
      setIsTitleValid(false);
      setErrorMessage("Por favor, preencha o campo Nome");
      setOpenErrorAlert(true);
      return;
    }

    createCategory(isEditing, {
      id: isEditing ? category.id : uuidv4(),
      title: title,
      color_hex: hexColor,
      fk_user: isEditing ? category.fk_user : null,
      categoryProductsToCreate: productsList.filter(
        (product) => product.checked && !product.existed
      ),
      categoryProductsToDelete: categoriesListToDelete,
    });

    closeModal();
  };

  if (loading && !productsList.length) {
    return (
      <AnimatedModal isOpen={show} onClose={closeModal} zIndex={5000}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      </AnimatedModal>
    );
  }

  return (
    <AnimatedModal isOpen={show} onClose={closeModal} zIndex={5000}>
      <ModalHeader
        title={isEditing ? category.title : "Nova Categoria"}
        icon={CategoryIcon}
        onClose={closeModal}
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": {
              minHeight: "64px",
              fontWeight: 600,
            },
          }}
        >
          <Tab
            icon={<CategoryIcon />}
            label="Informações"
            id="category-tab-0"
            aria-controls="category-tabpanel-0"
          />
          <Tab
            icon={<InventoryIcon />}
            label="Produtos"
            id="category-tab-1"
            aria-controls="category-tabpanel-1"
          />
        </Tabs>
      </Box>

      <ModalContent padding={0}>
        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.light, 0.05),
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    id="category_name"
                    name="title"
                    label="Nome da Categoria"
                    fullWidth
                    required
                    error={!isTitleValid && submitted}
                    value={title}
                    helperText={
                      !isTitleValid && submitted ? STRING_NOT_NULL_HELPER : ""
                    }
                    onChange={(event) => {
                      setTitle(event.target.value);
                      if (event.target.value) {
                        setIsTitleValid(true);
                      }
                    }}
                    InputProps={{
                      sx: { borderRadius: 2 },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider>
                    <Chip
                      icon={<PaletteIcon />}
                      label="Cor da Categoria"
                      color="primary"
                      variant="outlined"
                    />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        bgcolor: "background.paper",
                      }}
                    >
                      <ColorPicker value={hexColor} onChange={setHexColor} />
                    </Box>

                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: hexColor,
                          border: "1px solid rgba(0,0,0,0.1)",
                        }}
                      />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Cor selecionada
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {hexColor.toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </TabPanel>

        {/* Products Tab */}
        <TabPanel value={tabValue} index={1}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={searchProducts}
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FormControl
                sx={{ flex: 1 }}
                variant="outlined"
                onSubmit={searchProducts}
              >
                <InputLabel htmlFor="search-category-products-input">
                  Pesquisar produtos
                </InputLabel>
                <OutlinedInput
                  id="search-category-products-input"
                  type="text"
                  label="Pesquisar produtos"
                  onChange={(event) => {
                    setProductTitle(event.target.value);
                  }}
                  sx={{ borderRadius: 2 }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="search products"
                        onClick={searchProducts}
                        edge="end"
                        type="submit"
                        color="primary"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                maxHeight: "500px",
                overflow: "auto",
              }}
            >
              {productsList.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    opacity: 0.7,
                  }}
                >
                  <InventoryIcon sx={{ fontSize: 48, opacity: 0.6, mb: 2 }} />
                  <Typography variant="body1">
                    Nenhum produto encontrado
                  </Typography>
                </Box>
              ) : (
                productsList.map((product, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.light, 0.1),
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <ItemRowWithCheckbox
                        isSelected={product.checked}
                        setIsSelected={handleIsChecked}
                        product={product}
                        itemIndex={index}
                      />
                      <Divider variant="middle" />
                    </Box>
                  </motion.div>
                ))
              )}
            </Box>
          </motion.div>
        </TabPanel>
      </ModalContent>

      <ModalFooter>
        <Box sx={{ width: "100%" }}>
          <Collapse in={openErrorAlert}>
            <Alert
              variant="filled"
              severity="warning"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpenErrorAlert(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              {errorMessage}
            </Alert>
          </Collapse>

          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button
              variant="outlined"
              onClick={closeModal}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>

            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                startIcon={isEditing ? <EditIcon /> : <AddIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  bgcolor: hexColor,
                  "&:hover": {
                    bgcolor: alpha(hexColor, 0.8),
                  },
                }}
              >
                {isEditing ? "Editar Categoria" : "Criar Categoria"}
              </Button>
            </motion.div>
          </Stack>
        </Box>
      </ModalFooter>
    </AnimatedModal>
  );
}
